import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Loading from 'components/global/Loading';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useRefreshToken from 'hooks/useRefreshToken';

import axios from 'services/axios';
import {
	BOXES,
	BRANCHES,
	BRAND,
	COMPANY,
	EMAIL,
	ONBOARDING,
	ORDER_ACTIONS,
	OTHER_INTEGRATIONS,
	OTOFLEX,
	PRICE_CONFIGURATIONS,
	PRINT,
	PRODUCTS,
	PROFILE,
	SALES_CHANNELS,
	SHIPPING_AUTOMATION,
	SHIPPING_PARTNERS,
	SMS,
	USERS,
	WAREHOUSES,
	WHATSAPP,
	WMS_MODULE
} from 'services/consts';

import AppLogger from './AppLogger';
import { store } from './configureStore';

const parseJSON = (str) => {
	try {
		const obj = JSON.parse(str);
		return obj;
	} catch {
		return {};
	}
};

const onboardingServices = {
	[BRANCHES.ADD_BRANCH]: {
		name: 'branchCheck',
		validateFn: () => !store.getState().instruction?.branchCheck
	},
	[WAREHOUSES.ADD_WAREHOUSE]: {
		name: 'warehouseCheck',
		validateFn: () => !store.getState().instruction?.warehouseCheck
	},
	[WAREHOUSES.UPDATE_PICKUP_LOCATION]: {
		name: 'warehouseCheck',
		validateFn: () => !store.getState().instruction?.warehouseCheck
	},
	[WAREHOUSES.ADD_SENDER_LOCATION]: {
		name: 'warehouseCheck',
		validateFn: () => !store.getState().instruction?.warehouseCheck
	},
	[ORDER_ACTIONS.ADD_ORDER]: {
		name: 'orderCheck',
		validateFn: () => !store.getState().instruction?.orderCheck
	},
	[ORDER_ACTIONS.ADD_SIMPLE_ORDER]: {
		name: 'orderCheck',
		validateFn: () => !store.getState().instruction?.orderCheck
	},
	[BOXES.ADD_BOX]: {
		name: 'boxDimensionCheck',
		validateFn: () => !store.getState().instruction?.boxDimensionCheck
	},
	[PRINT.PRINT_AWB_ACTION]: {
		name: 'printAWBCheck',
		validateFn: () => !store.getState().instruction?.printAWBCheck
	},
	[SALES_CHANNELS.ADD_SALES_CHANNEL_CREDENTIALS]: {
		name: 'salesChannelCheck',
		validateFn: () => !store.getState().instruction?.salesChannelCheck
	},
	[SALES_CHANNELS.AUTHORIZE_SALES_CHANNEL]: {
		name: 'salesChannelCheck',
		validateFn: () => !store.getState().instruction?.salesChannelCheck
	},
	[SALES_CHANNELS.GET_SALE_CHANNEL_BY_COMPANY]: {
		name: 'salesChannelCheck',
		validateFn: (data) => !store.getState().instruction?.salesChannelCheck && data?.length > 0
	},
	[PRICE_CONFIGURATIONS.GET_PRICE_LIST]: {
		name: 'calculatePriceCheck',
		validateFn: () => !store.getState().instruction?.calculatePriceCheck
	},
	[ORDER_ACTIONS.CREATE_SHIPMENT]: [
		{
			name: 'shipmentCheck',
			validateFn: () => !store.getState().instruction?.shipmentCheck
		},
		{
			name: 'secondShipmentCheck',
			validateFn: () =>
				store.getState().instruction?.shipmentCheck && !store.getState().instruction?.secondShipmentCheck
		}
	],
	[COMPANY.GET_REMAINING_AMOUNT]: {
		name: 'walletCheck',
		validateFn: (data) => !store.getState().instruction?.walletCheck && data?.remainingAmount > 0
	},
	[SHIPPING_PARTNERS.ADD_DELIVERY_CHANNEL_SETTINGS]: {
		name: 'dcConnectionCheck',
		validateFn: () => !store.getState().instruction?.dcConnectionCheck
	},
	[OTHER_INTEGRATIONS.ADD_SMS_PROVIDER]: {
		name: 'smsProviderCheck',
		validateFn: () => !store.getState().instruction?.smsProviderCheck
	},
	[OTOFLEX.UPDATE_OTO_FLEX_SETTINGS]: {
		name: 'otoFlexCheck',
		validateFn: () => !store.getState().instruction?.otoFlexCheck
	},
	[PRINT.UPDATE_PRINT_SETTINGS]: {
		name: 'printSettingsCheck',
		validateFn: () => !store.getState().instruction?.printSettingsCheck
	},
	[USERS.ADD_USER]: {
		name: 'userCheck',
		validateFn: () => !store.getState().instruction?.userCheck
	},
	[PRODUCTS.ADD_PRODUCT]: {
		name: 'productCheck',
		validateFn: () => !store.getState().instruction?.productCheck
	},
	[WMS_MODULE.ADD_INVENTORY]: {
		name: 'inventoryCheck',
		validateFn: () => !store.getState().instruction?.inventoryCheck
	},
	[PROFILE.UPDATE_COMPANY_MODULE]: [
		{
			name: 'WMSModuleCheck',
			validateFn: (data) =>
				!store.getState().instruction?.WMSModuleCheck && data?.moduleId === 60 && data?.status === 'active'
		},
		{
			name: 'WMSAdvanceModuleCheck',
			validateFn: (data) =>
				!store.getState().instruction?.WMSAdvanceModuleCheck &&
				data?.moduleId === 69 &&
				data?.status === 'active'
		}
	]
};

const addEventServices = {
	[ORDER_ACTIONS.ADD_ORDER]: {
		name: 'addManualOrder',
		getProperties: (data, body) => ({
			destinationCity: body?.address?.cityName,
			destinationCountry: body?.address?.countryName,
			lat: body?.address?.lat,
			lng: body?.address?.lng,
			shipmentWeight: body?.weight,
			productsAdded: JSON.stringify(body?.items),
			paymentMethod: body?.paymentMethod,
			packageCount: body?.packageCount
		}),
		platforms: ['mixpanel']
	},
	[WAREHOUSES.ADD_WAREHOUSE]: {
		name: 'warehouseAdded',
		getProperties: (data, body) => ({
			city: body?.address?.cityName,
			country: body?.address?.countryName,
			source: 'dashboard'
		}),
		platforms: ['mixpanel']
	},
	[BRANCHES.ADD_BRANCH]: {
		name: 'branchAdded',
		getProperties: (data, body) => ({
			city: body?.address?.cityName,
			country: body?.address?.countryName,
			source: 'dashboard'
		}),
		platforms: ['mixpanel']
	},
	[BOXES.ADD_BOX]: {
		name: 'boxAdded',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[OTHER_INTEGRATIONS.ADD_SMS_PROVIDER]: {
		name: 'smsConnectionAdded',
		getProperties: () => ({})
	},
	[BRAND.ADD_CUSTOMIZABLE_PAGE]: {
		name: 'customizablePageAdded',
		getProperties: (data, body) => ({
			type: body?.smsType,
			language: body?.language,
			template: parseJSON(body?.templatePropJSON)?.templateName
		}),
		platforms: ['mixpanel']
	},
	[SMS.ADD_CONTENT_PREFERENCES]: {
		name: 'advertismentAdded',
		getProperties: (data, body) => ({
			language: body?.languageType,
			shownOn: body?.showOnScreen
		}),
		platforms: ['mixpanel']
	},
	[PRODUCTS.ADD_PRODUCT]: {
		name: 'productAdded',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[PRODUCTS.PRODUCT_SYNC_ALL_CHANNELS]: {
		name: 'syncronizedProducts',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[PRODUCTS.PRODUCT_SYNC_BY_SALES_CHANNEL_ID]: {
		name: 'syncronizedProducts',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[WAREHOUSES.UPDATE_PICKUP_LOCATION]: {
		name: 'updatePickupLocation',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[PRINT.PRINT_AWB_ACTION]: {
		name: 'AWBPrinted',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[PRINT.GET_ORDER_PRINT_DATA_ACTION]: {
		name: 'orderDetailsPrinted',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[USERS.ADD_USER]: {
		name: 'newUserAdded',
		getProperties: (data, body) => ({
			userType: body?.userType
		}),
		platforms: ['mixpanel']
	},
	[WHATSAPP.SAVE_WHATSAPP_INTEGRATION]: {
		name: 'whatsappConnectionAdded',
		getProperties: (data) => ({
			status: data?.success
		}),
		platforms: ['mixpanel']
	},
	[EMAIL.ADD_EMAIL_ACCOUNT]: {
		name: 'emailAccountsAdded',
		getProperties: (data, body) => ({
			host: body?.host
		}),
		platforms: ['mixpanel']
	},
	[SHIPPING_AUTOMATION.ADD_FEASIBILITY_RULE]: {
		name: 'feasibilityRuleAdded',
		getProperties: (data, body) => ({
			status: body?.status
		}),
		platforms: ['mixpanel']
	},
	[SHIPPING_AUTOMATION.ADD_PRIORITY_RULE]: {
		name: 'prioriryRuleAdded',
		getProperties: (data, body) => ({
			status: body?.status
		}),
		platforms: ['mixpanel']
	},
	[SHIPPING_AUTOMATION.GET_AUTOMATION_TEST_RESULT]: {
		name: 'otomationTestUsed',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[BRAND.ADD_BRAND]: {
		name: 'brandAdded',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[OTOFLEX.UPDATE_OTO_FLEX_SETTINGS]: {
		name: 'OTOFlexActivated',
		validateFn: (data, body) => body?.activate,
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[ORDER_ACTIONS.SYNCHRONIZE_ORDER]: {
		name: 'syncOrdersSalesChannels',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[SALES_CHANNELS.SAVE_LOCATION_MAPPING]: {
		name: 'matchPickupLocationsSalesChannels',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	},
	[ONBOARDING.COMPLETE_VIDEO_REWARD]: {
		name: 'videoRewardClaimed',
		getProperties: () => ({}),
		platforms: ['mixpanel']
	}
};

const addDataLayerServices = {
	[ORDER_ACTIONS.CREATE_SHIPMENT]: {
		name: 'shipment_created'
	},
	[ORDER_ACTIONS.CREATE_SHIPMENT_AUTOMATICALLY]: {
		name: 'shipment_created'
	},
	[ORDER_ACTIONS.CREATE_SHIPMENT_AND_PRINT]: {
		name: 'shipment_created'
	}
};

const AxiosInterceptor = ({ children }) => {
	const refreshAccess = useRefreshToken();
	const [loading, setLoading] = useState(true);
	const { updateOnboarding, track } = useAppTracking();
	const { showError, showUpdate } = useAlert();
	const showedUpdate = useRef(false);
	const userData = useSelector((state) => state.user);

	const responseInterceptor = async (error) => {
		const prevRequest = error?.config;
		if ((error?.response?.status === 401 || error?.response?.status === 403) && !prevRequest?.sent) {
			const ACCESS_TOKEN = await refreshAccess();
			if (ACCESS_TOKEN === false) {
				return Promise.reject(error);
			}

			if (import.meta.env.VITE_BRANCH_NAME) {
				prevRequest.headers['x-forwarded-authorization'] = `Bearer ${ACCESS_TOKEN}`;
			} else {
				prevRequest.headers.authorization = `Bearer ${ACCESS_TOKEN}`;
			}

			prevRequest.sent = true;
			return axios(prevRequest);
		}

		if (error?.response?.status === 429) {
			showError({
				header: 'errorMsg.tooManyRequestsHeader',
				message: 'errorMsg.tooManyRequestsMessage'
			});
		}
		return Promise.reject(error);
	};

	useEffect(() => {
		const defaultInterceptor = axios.interceptors.response.use((response) => {
			const body = parseJSON(response.config.data);
			onboardingListener(response.config.url, response.data);
			trackEventListener(response.config.url, response.data, body, response.status);
			addDataLayer(response.config.url, response.data, response.status);
			const isDevelopment = import.meta.env.VITE_APP_VERSION === 'local';
			const responseURL = response.config.baseURL;
			const apiURL = import.meta.env.VITE_API_URL;
			if (isDevelopment || responseURL !== apiURL || window.self !== window.top) return response;

			try {
				const appVersion = Number(response.headers['x-app-version']?.split('.')[2]);
				const localVersion = Number(import.meta.env.VITE_APP_VERSION?.split('.')[2]);

				if (!isNaN(appVersion) && !isNaN(localVersion) && appVersion > localVersion) {
					if (!showedUpdate.current) {
						showedUpdate.current = true;

						showUpdate({
							header: 'newVersionFoundTitle',
							message: 'newVersionSubtitle',
							persist: true,
							link: 'refreshToUpdate',
							linkAction: () => window.location.reload(false)
						});
					}
				}
			} catch (error) {
				AppLogger.error('Error in NewUpdateFound: ', error);
			}

			return response;
		}, responseInterceptor);

		setLoading(false);

		return () => {
			axios.interceptors.response.eject(defaultInterceptor);
		};
	}, [userData]);

	const onboardingListener = async (request, data) => {
		const field = onboardingServices[request];

		const datalayerName = {
			warehouseCheck: 'first_warehouse_added',
			walletCheck: 'wallet_first_charge',
			shipmentCheck: 'first_shipment',
			secondShipmentCheck: 'second_shipment'
		};

		const checkField = (field) => {
			if (field?.name) {
				if (field?.validateFn && !field?.validateFn(data)) return true;
				window.dataLayer.push({
					/* eslint-disable camelcase */
					event: datalayerName[field?.name],
					first_name: userData?.firstName || '',
					last_name: userData?.lastName || '',
					phone: userData?.phone || '',
					email: userData?.email || '',
					plan: userData?.planName || '',
					country: userData?.localization?.label || '',
					userID: userData?.ID || '',
					...(field?.name === 'first_shipment' || field?.name === 'second_shipment'
						? {
								shipping_company: data?.shipments?.[0]?.dcName || ''
							}
						: {}),
					...(field?.name === 'wallet_first_charge'
						? {
								value: data?.remainingAmount || 0,
								currency: userData?.localization?.currency || ''
							}
						: {})
					/* eslint-enable camelcase */
				});
				updateOnboarding([field.name]);
			}
		};

		if (Array.isArray(field)) {
			field.forEach((f) => checkField(f));
		} else {
			checkField(field);
		}
	};

	const trackEventListener = async (request, data, body, status) => {
		const field = addEventServices[request];

		const checkField = (field) => {
			if (field?.name) {
				if (field?.validateFn && !field?.validateFn(data, body)) return true;
				if (status !== 200) return false;
				const eventData = field.getProperties(data, body, status);
				track(field.name, eventData, field.platforms);
			}
		};

		checkField(field);
	};

	const addDataLayer = (request, data, status) => {
		const field = addDataLayerServices[request];

		if (field?.name) {
			if (status !== 200) return false;
			window.dataLayer.push({
				/* eslint-disable camelcase */
				event: field.name,
				first_name: userData?.firstName || '',
				last_name: userData?.lastName || '',
				phone: userData?.phone || '',
				email: userData?.email || '',
				plan: userData?.planName || '',
				country: userData?.localization?.label || '',
				userID: userData?.ID || '',
				...(field.name === 'shipment_created'
					? {
							shipping_company: data?.shipments?.[0]?.dcName || ''
						}
					: {})
				/* eslint-enable camelcase */
			});
			updateOnboarding([field.name]);
		}
	};

	return loading ? <Loading /> : children;
};

export default AxiosInterceptor;
